/**
 * Un aviso de «las invitaciones a equipos ya no son las que eran».
 *
 * Existe por un desajuste concreto: las invitaciones pendientes se enseñan en
 * DOS sitios a la vez —la campana y el banner del panel— y cada uno las pide por
 * su cuenta al montarse. Al aceptar o rechazar desde el banner, la campana se
 * quedaba con su lista de antes: el aviso seguía ahí, y sólo desaparecía al
 * pulsar «Ver invitación», que es lo que la volvía a montar. O sea, había que
 * responder una invitación que ya se había respondido.
 *
 * ── Por qué esto y no otra cosa ─────────────────────────────────────────────
 *
 * Subir la lista a un contexto que envolviera la aplicación sería la solución de
 * manual, pero la campana y el banner no comparten un padre cercano —uno vive en
 * la barra y el otro dentro del taller— así que el proveedor tendría que ir en la
 * raíz, y entonces todo el panel se repinta cada vez que cambia algo de este
 * rincón.
 *
 * Aquí sólo viaja un número. Quien lo escucha vuelve a pedir SUS datos, así que
 * cada pantalla sigue siendo dueña de lo suyo y lo único compartido es el hecho
 * de que algo pasó. `useSyncExternalStore` es la forma que React trae para
 * exactamente esto.
 */
import { useSyncExternalStore } from 'react'

let revision = 0
const listeners = new Set<() => void>()

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

function getSnapshot(): number {
  return revision
}

/**
 * Que todo el mundo se vuelva a preguntar qué invitaciones hay.
 *
 * Se llama al aceptar y al rechazar. No lleva datos a propósito: quien escucha
 * ya sabe pedir los suyos, y pasarle la lista aquí obligaría a que las dos
 * pantallas se pusieran de acuerdo en su forma.
 */
export function notifyInvitesChanged(): void {
  revision += 1
  listeners.forEach((listener) => { listener() })
}

/** Cambia cuando algo respondió una invitación. Sirve de dependencia de efecto. */
export function useInvitesRevision(): number {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
